import { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Collapse } from '@mui/material';
import { colors, Typography } from '@sweep-io/sweep-design';
import { SweepSolid } from '@sweep-io/sweep-design/dist/icons';
import { CenteredCircularProgress } from '../../CenteredCircularProgress';
import { ReasoningChunk } from '../aiChatTypes';
import { ChevronUp, ChevronDown, CheckCircle } from '@sweep-io/sweep-design/dist/icons';
import Typewriter from '../../Typewriter';

interface ReasoningSectionProps {
  reasoningChunks: ReasoningChunk[];
  isStreamingReasoning?: boolean;
  resumeFromChunkIndex?: number;
  onChunkProgressUpdate?: (chunkIndex: number) => void;
}

export const ReasoningSection = ({
  reasoningChunks,
  isStreamingReasoning = false,
  resumeFromChunkIndex = 0,
  onChunkProgressUpdate,
}: ReasoningSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const prevStreamingRef = useRef(isStreamingReasoning);

  const toggleExpansion = () => setIsExpanded(!isExpanded);

  const isEmpty = reasoningChunks.length === 0;

  // Handle chunk progress updates
  const handleChunkProgress = useCallback(
    (chunkIndex: number) => {
      onChunkProgressUpdate?.(chunkIndex);
    },
    [onChunkProgressUpdate],
  );

  // Track current chunk progress during streaming
  useEffect(() => {
    if (isStreamingReasoning && reasoningChunks.length > 0) {
      const currentIndex = Math.max(reasoningChunks.length - 1, resumeFromChunkIndex);
      handleChunkProgress(currentIndex);
    }
  }, [reasoningChunks.length, isStreamingReasoning, resumeFromChunkIndex, handleChunkProgress]);

  // Track streaming state changes
  useEffect(() => {
    const wasStreaming = prevStreamingRef.current;
    const isNowStreaming = isStreamingReasoning;

    if (wasStreaming && !isNowStreaming) {
      setTimeout(() => {
        setIsExpanded(false);
      }, 1500);
    }

    prevStreamingRef.current = isStreamingReasoning;
  }, [isStreamingReasoning]);

  return (
    <Box className="reasoning-section" sx={{ mb: '4px' }}>
      {isEmpty && (
        <Box pl={1.5}>
          <CenteredCircularProgress size={16} color={colors.grey[800]} />
        </Box>
      )}
      {!isEmpty && (
        <>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              cursor: 'pointer',
              position: 'relative',
              left: '-4px',
            }}
            onClick={toggleExpansion}
          >
            {isStreamingReasoning ? (
              <CenteredCircularProgress size={16} color={colors.grey[800]} />
            ) : (
              <SweepSolid size="medium" />
            )}

            <Box display="flex" alignItems="center" gap={0.5}>
              {isStreamingReasoning && resumeFromChunkIndex === 0 && (
                <Typewriter variant="body" color={colors.grey[800]} autoStart={true}>
                  Analyzing metadata
                </Typewriter>
              )}
              {isStreamingReasoning && resumeFromChunkIndex !== 0 && (
                <Typography variant="body" color={colors.grey[800]}>
                  Analyzing metadata
                </Typography>
              )}
              {!isStreamingReasoning && (
                <Typography variant="body" color={colors.grey[800]}>
                  Show reasoning
                </Typography>
              )}

              <Box
                position="relative"
                sx={{
                  position: 'relative',
                  top: '1px',
                }}
                display="flex"
              >
                {isExpanded ? <ChevronDown size="small" /> : <ChevronUp size="small" />}
              </Box>
            </Box>
          </Box>
          <Collapse in={isExpanded}>
            <Box mt={1.5}>
              {reasoningChunks.map((chunk, index) => {
                // Determine if this chunk should animate
                const shouldAnimate = isStreamingReasoning && index >= resumeFromChunkIndex;

                return (
                  <Box
                    key={chunk + index}
                    display="flex"
                    alignItems="flex-start"
                    gap={1}
                    sx={{
                      position: 'relative',
                      pb: 2,
                    }}
                  >
                    {/* Bullet point */}
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: colors.grey[500],
                        mt: '5px', //to be in the vertically centered with the text
                        flexShrink: 0,
                        position: 'relative',
                        zIndex: 2,
                      }}
                    />

                    {/* Vertical line connecting bullet points */}
                    {index <
                      (isStreamingReasoning
                        ? reasoningChunks.length - 1
                        : reasoningChunks.length) && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: '2.5px',
                          top: '13px',
                          bottom: index < reasoningChunks.length - 1 ? '-3.5px' : '-1.5px',
                          width: '1px',
                          backgroundColor: colors.grey[500],
                          zIndex: 1,
                        }}
                      />
                    )}
                    {shouldAnimate ? (
                      <Typewriter variant="caption" color={colors.grey[700]} autoStart={true}>
                        {chunk}
                      </Typewriter>
                    ) : (
                      <Typography variant="caption" color={colors.grey[700]}>
                        {chunk}
                      </Typography>
                    )}
                  </Box>
                );
              })}

              {/*Done bullet:*/}
              {!isStreamingReasoning && (
                <Box
                  display="flex"
                  alignItems="flex-start"
                  gap="5px"
                  sx={{
                    position: 'relative',
                    left: '-2.5px',
                  }}
                >
                  <Box
                    sx={{
                      mt: '2px',
                      display: 'flex',
                    }}
                  >
                    <CheckCircle size="small" color={colors.grey[500]} />
                  </Box>

                  <Typography variant="caption" color={colors.grey[700]}>
                    Done
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </>
      )}
    </Box>
  );
};
